import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  ErrorMessage,
  Field,
  FieldGroup,
} from "../../design/fieldset";
import { Input } from "../../design/input";
import { Subheading } from "../../design/heading";
import { useBusiness } from "../../contexts/BusinessContext";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { storage } from "../../firebase";
import { Textarea } from "../../design/textarea";
import PhoneNumberInput from "../PhoneNumberInput";
import { useAccount } from "../../contexts/AccountContext";
import { useAuth } from "../../contexts/AuthContext";
import LogoUpload from "../LogoUpload";
import { Text } from "../../design/text";

export interface BusinessFormRef {
  submitForm: () => Promise<void>;
}

interface Props {
  isSaving?: boolean;
  onSuccessfulSubmit?: () => void;
}

interface Errors {
  name?: string;
  website?: string;
  about?: string;
  phoneNumber?: string;
  email?: string;

  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface BusinessUpdateData {
  name?: string;
  website?: string;
  about?: string;
  phoneNumber?: string;
  email?: string;
  logoUrl?: string;

  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

const BusinessForm = forwardRef<BusinessFormRef, Props>(
  ({ onSuccessfulSubmit }, ref) => {
    const {account} = useAccount();
    const {user} = useAuth();
    const { business, updateBusiness } = useBusiness();
    const [name, setName] = useState(business?.name ?? "");
    const [website, setWebsite] = useState(business?.website ?? "");
    const [about, setAbout] = useState(business?.about ?? "");
    const [phoneNumber, setPhoneNumber] = useState(business?.phoneNumber ?? "");
    const [email, setEmail] = useState(business?.email ?? "");
    const [errors, setErrors] = useState<Errors>({});
    const [logo, setLogo] = useState<File | null>(null);

    useEffect(() => {
      setName(business?.name ?? "");
      setWebsite(business?.website ?? "");
      setAbout(business?.about ?? "");
      setPhoneNumber(business?.phoneNumber ?? "");
      setEmail(business?.email ?? "");
    }, [business]);

    const validateForm = () => {
      const newErrors: { [key: string]: string } = {};
      if (!name) {
        newErrors.name = "Name is required";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    useImperativeHandle(ref, () => ({
      submitForm,
    }));

    const submitForm = async () => {
      if (!validateForm()) {
        return;
      }
      await user?.getIdToken(true);
      const id = business?.id ?? account?.id;
      if (!id) {
        console.error("No business or account ID available");
        return;
      }
      try {
        let cleanData: BusinessUpdateData = {
          name,
          website,
          about,
          phoneNumber: `${phoneNumber.replace(/\D/g, "")}`,
          email,
        };
        if (logo) {
          const fileRef = storageRef(
            storage,
            `businesses/${id}/${logo.name}`,
          );
          await uploadBytes(fileRef, logo);
          const url = await getDownloadURL(fileRef);
          cleanData = { ...cleanData, logoUrl: url };
        }
        await updateBusiness(id, cleanData);
        onSuccessfulSubmit?.();
      } catch (e) {
        console.error("Error creating visit:", e);
      }
    };

    return (
      <form onSubmit={submitForm}>
        <FieldGroup>
          {/* Logo Upload */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Logo</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                Your logo will appear on reports and invoices
              </Text>
            </div>
            <div>
              <LogoUpload
                currentLogoUrl={business?.logoUrl}
                onFileSelected={setLogo}
              />
            </div>
          </section>

          {/* Business Name */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Business Name</Subheading>
            </div>
            <div>
              <Field>
                <Input
                  id="businessName"
                  name="businessName"
                  placeholder="Business Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>
            </div>
          </section>
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Business Website</Subheading>
            </div>
            <div>
              <Field>
                <Input
                  id="businessWebsite"
                  type="url"
                  name="businessWebsite"
                  placeholder="Business Website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
                {errors.website && (
                  <ErrorMessage>{errors.website}</ErrorMessage>
                )}
              </Field>
            </div>
          </section>
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Business About</Subheading>
            </div>
            <div>
              <Field>
                <Textarea
                  id="businessAbout"
                  name="businessAbout"
                  placeholder="Business About"
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                />
                {errors.about && <ErrorMessage>{errors.about}</ErrorMessage>}
              </Field>
            </div>
          </section>
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Business Phone Number</Subheading>
            </div>
            <div>
              <Field>
                <PhoneNumberInput
                  defaultValue={phoneNumber}
                  placeholder="Business Phone Number"
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value)}
                />
                {errors.phoneNumber && (
                  <ErrorMessage>{errors.phoneNumber}</ErrorMessage>
                )}
              </Field>
            </div>
          </section>
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Business Email</Subheading>
            </div>
            <div>
              <Field>
                <Input
                  id="businessEmail"
                  name="businessEmail"
                  placeholder="Business Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>
            </div>
          </section>
        </FieldGroup>
      </form>
    );
  },
);

export default BusinessForm;
